import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner, Modal, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaTimes, FaDownload, FaShareAlt } from 'react-icons/fa';
import { generatePdfForDte } from '../../../../../../../../../../../../../../crud/payments_api/dte';
import './RenderPdf.scss';

interface RenderPdfProps {
  show: boolean;
  onHide: () => void;
  dteId: number;
  localId: number;
  folio: number;
  tipoDte: number;
  defaultFormat?: 'carta' | '80mm';
  autoPrint?: boolean;
}

const DTE_TYPES: { [key: number]: string } = {
  33: 'Factura Electrónica',
  34: 'Factura No Afecta o Exenta',
  39: 'Boleta Electrónica',
  41: 'Boleta Exenta',
  46: 'Factura de Compra',
  52: 'Guía de Despacho',
  56: 'Nota de Débito',
  61: 'Nota de Crédito'
};

const RenderPdf: React.FC<RenderPdfProps> = ({ show, onHide, dteId, localId, folio, tipoDte, defaultFormat = 'carta', autoPrint = false }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfFormat, setPdfFormat] = useState<'carta' | '80mm'>(defaultFormat);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // One-shot guard so auto-print only fires once per opened document.
  const autoPrintedRef = useRef<boolean>(false);

  useEffect(() => {
    if (show && dteId) {
      loadPdf();
    }
    
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dteId, pdfFormat]);

  const loadPdf = async () => {
    setLoading(true);
    // Allow auto-print to fire once for this freshly loaded document.
    autoPrintedRef.current = false;
    // Clear previous PDF
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPdfBase64(null);
    
    try {
      // Only send options for 80mm format
      const options = pdfFormat === '80mm' ? { formato: '80mm' as const } : undefined;
      const response = await generatePdfForDte(localId, dteId, options);
      
      if (response.data.pdf_content_base64) {
        const base64 = response.data.pdf_content_base64;
        setPdfBase64(base64);
        
        // Convert base64 to blob URL
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } else {
        toast.error('No se pudo cargar el PDF');
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Error al cargar el documento PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBase64) return;

    try {
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `DTE_${tipoDte}_Folio_${folio}${pdfFormat === '80mm' ? '_80mm' : ''}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      toast.success('Documento descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el documento');
    }
  };

  const handleShare = async () => {
    if (!pdfBase64) return;

    const dteType = DTE_TYPES[tipoDte] || `Tipo ${tipoDte}`;
    
    if (navigator.share) {
      try {
        // Convert base64 to file
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const file = new File([blob], `DTE_${tipoDte}_Folio_${folio}${pdfFormat === '80mm' ? '_80mm' : ''}.pdf`, { type: 'application/pdf' });

        await navigator.share({
          title: `${dteType} - Folio ${folio}`,
          text: `Documento Tributario Electrónico: ${dteType}`,
          files: [file]
        });
        
        toast.success('Documento compartido exitosamente');
      } catch (error) {
        console.error('Error sharing:', error);
        toast.error('Error al compartir el documento');
      }
    } else {
      toast.info('La función de compartir no está disponible en este navegador');
    }
  };

  const dteType = DTE_TYPES[tipoDte] || `Tipo ${tipoDte}`;

  // Best-effort auto-print: trigger the browser print dialog once the PDF iframe loads.
  // May no-op in some browsers/PDF viewers; the manual Imprimir/Descargar buttons remain a fallback.
  const handleIframeLoad = () => {
    if (!autoPrint || autoPrintedRef.current || !pdfUrl) return;
    autoPrintedRef.current = true;
    setTimeout(() => {
      try {
        iframeRef.current?.contentWindow?.focus();
        iframeRef.current?.contentWindow?.print();
      } catch (error) {
        console.error('Auto-print failed; user can print manually:', error);
      }
    }, 300);
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="xl"
      centered
      className="render-pdf-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">{dteType} - Folio {folio}</h2>
        </div>
        <div className="pdf-format-toggle">
          <ButtonGroup>
            <ToggleButton
              id="format-carta"
              type="radio"
              variant={pdfFormat === 'carta' ? 'primary' : 'outline-secondary'}
              name="pdfFormat"
              value="carta"
              checked={pdfFormat === 'carta'}
              onChange={() => setPdfFormat('carta')}
              disabled={loading}
              size="sm"
            >
              Carta
            </ToggleButton>
            <ToggleButton
              id="format-80mm"
              type="radio"
              variant={pdfFormat === '80mm' ? 'primary' : 'outline-secondary'}
              name="pdfFormat"
              value="80mm"
              checked={pdfFormat === '80mm'}
              onChange={() => setPdfFormat('80mm')}
              disabled={loading}
              size="sm"
            >
              80mm
            </ToggleButton>
          </ButtonGroup>
        </div>
        <button className="modal-close-button" onClick={onHide}>
          <FaTimes />
        </button>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="pdf-loading">
            <Spinner animation="border" variant="primary" />
            <p>Cargando documento...</p>
          </div>
        ) : pdfUrl ? (
          <div className="pdf-viewer-container">
            <iframe
              ref={iframeRef}
              src={pdfUrl}
              title={`${dteType} - Folio ${folio}`}
              className="pdf-iframe"
              onLoad={handleIframeLoad}
            />
          </div>
        ) : (
          <div className="pdf-error">
            <p>No se pudo cargar el documento PDF</p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="form-modal-footer-custom">
        <div className="share-actions">
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <Button 
              variant="primary" 
              onClick={handleShare}
              disabled={!pdfBase64 || loading}
              className="share-button"
            >
              <FaShareAlt className="me-2" />
              Compartir
            </Button>
          )}
          
          <Button 
            variant="secondary" 
            onClick={handleDownload}
            disabled={!pdfBase64 || loading}
            className="share-button download-button"
          >
            <FaDownload className="me-2" />
            Descargar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RenderPdf;
